// src/utils/ledger/shared.js
// ============================================================
//  Shared utilities for Development Express Ledger PDFs
//  Used by both standard and premium report generators
// ============================================================

// ===== BRAND PALETTE =====
export const BRAND = {
  GOLD:       '#C9A84C',
  GOLD_LIGHT: '#F5EFD9',
  GOLD_PALE:  '#FBF8EC',
  GOLD_DARK:  '#8A7028',
  GOLD_DEEP:  '#5C4A18',

  INK:        '#0A0A0A',
  INK_SOFT:   '#2C2C2C',
  MUTED:      '#6B6B6B',
  MUTED_LT:   '#9B9B9B',
  BORDER:     '#E5E5E5',
  BORDER_LT:  '#F0F0F0',
  ROW_ALT:    '#FAF8F2',

  // Status palette
  EMERALD:    '#0E7C3A',
  EMERALD_LT: '#D4EFDF',
  RUBY:       '#B5341B',
  RUBY_LT:    '#F8DDD4',
  SAPPHIRE:   '#1E4D8C',
  SAPPHIRE_LT:'#D4E1F2',
  AMBER:      '#B8801A',
  AMBER_LT:   '#FCEFD4',

  // Cover page (premium only)
  COVER_BG:      '#0F0E0A',
  COVER_GOLD:    '#D4B45A',
  COVER_GOLD_LT: '#E8D49A',
};

// Hex -> RGB array (jsPDF wants [r, g, b])
export const rgb = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

// ===== FORMATTERS =====

// Indian rupee format: Rs 7,57,500
// Note: Using "Rs " instead of ₹ (U+20B9) because the default Helvetica
// font in jsPDF does not include the rupee glyph. To use ₹, embed a custom
// TTF font like Noto Sans Devanagari via doc.addFileToVFS() + addFont().
export const fmtINR = (n) => {
  if (n === null || n === undefined || isNaN(n)) return 'Rs 0';
  if (n === 0) return 'Rs 0';
  const abs = Math.abs(n);
  const s = abs.toFixed(0);
  // Indian grouping: last 3 digits, then groups of 2
  let result;
  if (s.length <= 3) {
    result = s;
  } else {
    const last3 = s.slice(-3);
    const rest = s.slice(0, -3);
    const grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    result = grouped + ',' + last3;
  }
  return `Rs ${result}`;
};

// Format with brackets for negative (debits)
export const fmtINRSigned = (n) => {
  if (n < 0) return `(${fmtINR(n)})`;
  return fmtINR(n);
};

// Lakhs format for chart axis: 8.5L
export const fmtLakhs = (n) => `${(n / 100000).toFixed(1)}L`;

// Date formatter
export const fmtDate = (iso, short = false) => {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (short) {
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}`;
  }
  return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

export const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dd = String(d.getDate()).padStart(2,'0');
  const hh = String(d.getHours()).padStart(2,'0');
  const mm = String(d.getMinutes()).padStart(2,'0');
  return `${dd}-${months[d.getMonth()]}-${d.getFullYear()}, ${hh}:${mm} IST`;
};

const WORD_ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const WORD_TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
];

function wordsUnder100(n) {
  if (n < 20) return WORD_ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones ? `${WORD_TENS[tens]} ${WORD_ONES[ones]}` : WORD_TENS[tens];
}

function wordsUnder1000(n) {
  if (n < 100) return wordsUnder100(n);
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return rest
    ? `${WORD_ONES[hundreds]} Hundred ${wordsUnder100(rest)}`
    : `${WORD_ONES[hundreds]} Hundred`;
}

/** Indian numbering: Rupees Two Thousand Three Hundred Thirty Three Only */
export function amountInWordsINR(amount) {
  let n = Math.round(Number(amount) || 0);
  if (n === 0) return 'Rupees Zero Only';
  if (n < 0) n = Math.abs(n);

  const parts = [];
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;

  if (crore) parts.push(`${wordsUnder1000(crore)} Crore`);
  if (lakh) parts.push(`${wordsUnder100(lakh)} Lakh`);
  if (thousand) parts.push(`${wordsUnder100(thousand)} Thousand`);
  if (n) parts.push(wordsUnder1000(n));

  return `Rupees ${parts.join(' ')} Only`;
}

// ===== STATUS HELPERS =====
export const STATUS_STYLES = {
  ACTIVE:   { bg: BRAND.EMERALD_LT,   fg: BRAND.EMERALD },
  CLOSED:   { bg: '#EFEFEF',          fg: BRAND.MUTED   },
  DISPUTED: { bg: BRAND.RUBY_LT,      fg: BRAND.RUBY    },
  NEW:      { bg: BRAND.SAPPHIRE_LT,  fg: BRAND.SAPPHIRE},
  RECEIPT:  { bg: BRAND.EMERALD_LT,   fg: BRAND.EMERALD },
  ADJUSTED: { bg: BRAND.SAPPHIRE_LT,  fg: BRAND.SAPPHIRE},
  REFUND:   { bg: BRAND.RUBY_LT,      fg: BRAND.RUBY    },
};

// ===== TRANSACTION AGGREGATIONS =====

// Compute summary KPIs from transaction list
export const computeSummary = (transactions) => {
  let received = 0, adjusted = 0, refunded = 0;
  transactions.forEach(t => {
    if (t.type === 'RECEIPT')   received += Number(t.amount || 0);
    if (t.type === 'ADJUSTED')  adjusted += Number(t.amount || 0);
    if (t.type === 'REFUND')    refunded += Number(t.amount || 0);
  });
  const outstanding = received - adjusted - refunded;
  return { received, adjusted, refunded, outstanding };
};

// Group by client for client-wise summary
export const groupByClient = (transactions) => {
  const map = {};
  transactions.forEach(t => {
    const key = t.client_id || t.client_name;
    if (!map[key]) {
      map[key] = {
        client_name: t.client_name,
        project_site: t.project_site || '—',
        received: 0, adjusted: 0, refunded: 0,
        balance: 0, status: 'ACTIVE',
        last_txn: t.txn_date,
      };
    }
    const c = map[key];
    if (t.type === 'RECEIPT')  c.received += Number(t.amount || 0);
    if (t.type === 'ADJUSTED') c.adjusted += Number(t.amount || 0);
    if (t.type === 'REFUND')   c.refunded += Number(t.amount || 0);
    if (t.client_status) c.status = t.client_status;
    if (new Date(t.txn_date) > new Date(c.last_txn)) c.last_txn = t.txn_date;
  });
  Object.values(map).forEach(c => {
    c.balance = c.received - c.adjusted - c.refunded;
  });
  return Object.values(map);
};

// Mode-wise grouping (for receipt channel mix)
export const groupByMode = (transactions) => {
  const map = {};
  transactions
    .filter(t => t.type === 'RECEIPT' && t.payment_mode)
    .forEach(t => {
      const m = t.payment_mode;
      if (!map[m]) map[m] = { mode: m, count: 0, total: 0 };
      map[m].count += 1;
      map[m].total += Number(t.amount || 0);
    });
  const total = Object.values(map).reduce((a, b) => a + b.total, 0);
  Object.values(map).forEach(m => {
    m.percent = total ? (m.total / total) * 100 : 0;
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
};

// Aging buckets — based on days outstanding from txn_date
export const computeAging = (transactions) => {
  const now = new Date();
  const buckets = {
    '0-30':  { count: 0, amount: 0, clients: new Set() },
    '31-60': { count: 0, amount: 0, clients: new Set() },
    '61-90': { count: 0, amount: 0, clients: new Set() },
    '90+':   { count: 0, amount: 0, clients: new Set() },
  };
  // Aggregate per client first
  const perClient = groupByClient(transactions);
  perClient.forEach(c => {
    if (c.balance <= 0) return;
    const days = Math.floor((now - new Date(c.last_txn)) / (1000 * 60 * 60 * 24));
    let bucket;
    if (days <= 30)      bucket = '0-30';
    else if (days <= 60) bucket = '31-60';
    else if (days <= 90) bucket = '61-90';
    else                 bucket = '90+';
    buckets[bucket].count += 1;
    buckets[bucket].amount += c.balance;
    buckets[bucket].clients.add(c.client_name);
  });
  return Object.entries(buckets).map(([label, data]) => ({
    label,
    count: data.count,
    amount: data.amount,
  }));
};

// ===== HASH (audit trail) =====
// Simple SHA-256-like hash for browser (using Web Crypto API)
export const computeDocHash = async (reportId, periodKey, txnCount) => {
  const text = `${reportId}-${periodKey}-${txnCount}-${Date.now()}`;
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }
  // Fallback - simple string hash
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = ((h << 5) - h) + text.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(16).toUpperCase().padStart(16, '0');
};

// ===== LOW-LEVEL CANVAS HELPERS =====

// Rounded rectangle fill
export const roundRectFill = (doc, x, y, w, h, r, color) => {
  doc.setFillColor(...rgb(color));
  doc.roundedRect(x, y, w, h, r, r, 'F');
};

// Rounded rectangle stroke
export const roundRectStroke = (doc, x, y, w, h, r, color, width = 0.2) => {
  doc.setDrawColor(...rgb(color));
  doc.setLineWidth(width);
  doc.roundedRect(x, y, w, h, r, r, 'S');
};

// Rounded rectangle filled + stroked
export const roundRectFS = (doc, x, y, w, h, r, fillColor, strokeColor, lineW = 0.2) => {
  doc.setFillColor(...rgb(fillColor));
  doc.setDrawColor(...rgb(strokeColor));
  doc.setLineWidth(lineW);
  doc.roundedRect(x, y, w, h, r, r, 'FD');
};

// Status pill renderer
export const drawStatusPill = (doc, x, y, w, h, text, bgColor, fgColor) => {
  roundRectFill(doc, x, y, w, h, h / 2, bgColor);
  doc.setTextColor(...rgb(fgColor));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(text.toUpperCase(), x + w / 2, y + h / 2 + 1.2, { align: 'center' });
};

// Simple sparkline drawer
export const drawSparkline = (doc, x, y, w, h, data, color) => {
  if (!data || data.length < 2) return;
  const mn = Math.min(...data);
  const mx = Math.max(...data);
  const rng = mx - mn || 1;
  const n = data.length;

  // Filled area
  const [r, g, b] = rgb(color);
  doc.setFillColor(r, g, b);
  doc.setGState(new doc.GState({ opacity: 0.15 }));
  const points = data.map((v, i) => ({
    x: x + (i / (n - 1)) * w,
    y: y + h - ((v - mn) / rng) * h,
  }));
  // Draw filled polygon
  doc.lines(
    [
      ...points.slice(1).map((p, i) => [
        p.x - points[i].x, p.y - points[i].y
      ]),
      [0, h - (points[points.length - 1].y - y)],
      [-(points[points.length - 1].x - points[0].x), 0],
    ],
    points[0].x, points[0].y,
    [1, 1], 'F'
  );
  doc.setGState(new doc.GState({ opacity: 1 }));

  // Line
  doc.setDrawColor(...rgb(color));
  doc.setLineWidth(0.6);
  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
  }

  // End dot
  doc.setFillColor(...rgb(color));
  doc.circle(points[n - 1].x, points[n - 1].y, 0.7, 'F');
};

// Diamond divider (decorative)
export const drawDiamond = (doc, cx, cy, size, color) => {
  doc.setFillColor(...rgb(color));
  doc.triangle(
    cx, cy - size,
    cx + size, cy,
    cx, cy + size,
    'F'
  );
  doc.triangle(
    cx, cy - size,
    cx - size, cy,
    cx, cy + size,
    'F'
  );
};

// ===== PDF PAGE LAYOUT (header/footer safe zones) =====
/** Standard tier: gold band header is 18mm; footer line ~15mm from bottom */
export const LEDGER_LAYOUT = {
  HEADER_H: 18,
  FOOTER_H: 15,
  SIDE: 15,
  /** autoTable top margin on continuation pages — must clear header band */
  TOP: 24,
  BOTTOM: 20,
  /** Manual text/cards on page 1 below title block */
  FIRST_PAGE_START: 26,
};

/** Premium inside pages: thin bar + text block ~22mm */
export const PREMIUM_LAYOUT = {
  HEADER_H: 22,
  FOOTER_H: 18,
  SIDE: 15,
  TOP: 26,
  BOTTOM: 22,
  FIRST_PAGE_START: 26,
};

export const ledgerMargins = (layout = LEDGER_LAYOUT) => ({
  top: layout.TOP,
  bottom: layout.BOTTOM,
  left: layout.SIDE,
  right: layout.SIDE,
});

export const pageBottomLimit = (doc, layout = LEDGER_LAYOUT) =>
  doc.internal.pageSize.getHeight() - layout.BOTTOM;

export const ensurePageSpace = (doc, y, neededMm, layout = LEDGER_LAYOUT) => {
  const remaining = pageBottomLimit(doc, layout) - y;
  if (neededMm > remaining) {
    doc.addPage();
    return layout.TOP;
  }
  return y;
};

/** @deprecated Prefer PdfLayoutEngine.ensureSpace for new ledger sections */

export const stampAllPages = (doc, drawFn, startPage = 1) => {
  const total = doc.internal.getNumberOfPages();
  for (let i = startPage; i <= total; i++) {
    doc.setPage(i);
    drawFn(doc, i, total);
  }
};
